<template>
  <div id="app">
    <TheNavBar />
    <div class="the-body" :class="navBarIsStickyClass">
      <div v-if="appReady">
        <UserWelcomeBar v-if="showWelcomeBar" :email="authUserEmail" />
        <UserImagesStatsBar :key="statsBarKey" />
      </div>

      <div v-if="showAppInactiveAlert" class="mt-4">
        <AlertCard type="error" header="Access Denied">
          The app is temporarily down from maintenance. Please try again later.
        </AlertCard>
      </div>

      <div v-if="showUserDisabledAlert" class="mt-4">
        <AlertCard type="error" header="Access Denied">
          Your account has been disabled.
        </AlertCard>
      </div>

      <div v-if="showUploadSvcInactiveAlert" class="mt-4">
        <AlertCard type="error" header="Upload Service Unavailable">
          The Image Upload service is temporarily down for maintenance. Please
          try again later.
        </AlertCard>
      </div>

      <router-view v-if="showContent" :key="viewKey" />

      <!-- <div
        v-if="authUser && !userEnabled && $route.path !== '/logout'"
        class="mt-4"
      >
        <AlertCard type="error" header="Access Denied">
          Your account has been disabled.
        </AlertCard>
      </div>
      <div v-else>
        <div
          v-if="!uploadSvcActive && /replace|upload/.test($route.path)"
          class="mt-4"
        >
          <AlertCard type="error" header="Upload Service Unavailable">
            The Image Upload service is temporarily down for maintenance. Please
            try again later.
          </AlertCard>
        </div>
        <router-view v-else :key="viewKey" />
      </div> -->
    </div>
    <TheFooter />
  </div>
</template>

<script>
import stateUtil from '@/helpers/stateUtil';

import TheNavBar from '@/components/layout/TheNavBar';
import UserWelcomeBar from '@/components/dashboard/UserWelcomeBar';
import UserImagesStatsBar from '@/components/dashboard/UserImagesStatsBar';
import AlertCard from '@/components/notifications/AlertCard';
import TheFooter from '@/components/layout/TheFooter';

import { mapGetters } from 'vuex';

export default {
  name: 'MainApp',
  components: {
    TheNavBar,
    UserWelcomeBar,
    UserImagesStatsBar,
    AlertCard,
    TheFooter,
  },
  data: function () {
    return {
      statsBarKey: 1,
      viewKeyStore: 1,
      //authUser: false
    };
  },

  // NOTE: **ALWAYS ALLOT LOGOUT** no matter if app is disabled, user account disabled, or both. Want to allow user
  // logout in event that they were logged in when either of these (app or user account) was set to disabled; e.g. what if
  // user on a public computer? When app or their account became activated again, assuming they logged out, another computer
  // user would NOT see their logged in dashboard. NOTE: Really need to address the whole session aspect -- assuming user closes
  // browser window, their session expires (unless we give option on login to "remember me").
  computed: {
    ...mapGetters('auth', ['authUser', 'authUserEmail']),
    ...mapGetters('uploadTracker', ['imageCountsLoaded']),
    ...mapGetters('layout', ['navBarSticky', 'showWelcomeBar']),
    ...mapGetters(['appActive', 'uploadSvcActive', 'userEnabled']),

    navBarIsStickyClass: function () {
      var classObj = {};
      if (this.navBarSticky) {
        classObj['the-body--sticky-nav-bar'] = true;
      }
      return classObj;
    },

    userAccountDisabled: function () {
      return this.authUser && !this.userEnabled;
    },
    userAccountActive: function () {
      return !!this.userAccountDisabled;
    },
    showContent: function () {
      if (this.showAppInactiveAlert) {
        // Whether auth user or not, allow home page content to show
        if (this.$route.path === '/') {
          return true;
        }
        return false;
      }
      if (this.showUserDisabledAlert) {
        return false;
      }
      if (this.showUploadSvcInactiveAlert) {
        return false;
      }
      return true;
    },
    showAppInactiveAlert: function () {
      // When on home page, do not show this alert (and note - see showContent() method -- we show the home page content); it's
      // all other routes where we show THIS 'app is disabled' alert
      if (this.$route.path === '/') {
        return false;
      }
      // Don't show if logout route is hit; and by not showing it, i.e. the return value is false, that makes showContent() return
      // true, which is what we want, i.e. want the logout page (component) to show (load) since that's where the logout logic is, i.e.
      // we WANT the user to be able to logout.
      if (this.$route.path === '/logout') {
        return false;
      }
      return !this.appActive;
    },
    showUserDisabledAlert: function () {
      // App being inactive takes precedence, i.e. show THAT inactive alert only, even if user's account is disabled.
      // Without this check, if app is disabled (inactive), BOTH *that* alert and this one will show, stacked.
      if (!this.appActive) {
        return false;
      }
      // If user is logged in and their account is disabled and they hit any route EXCEPT logout, show
      // the alert "You account has been disabled/suspended" and do NOT show the route content. If they hit logout,
      // let it happen.
      if (this.userAccountDisabled && this.$route.path !== '/logout') {
        return true;
      }
      return false;
    },

    showUploadSvcInactiveAlert: function () {
      // App being inactive takes precedence, i.e. show THAT inactive alert only, even if upload service is ALSO inactive.
      // Without this check, if app is disabled (inactive), BOTH *that* alert and this one will show, stacked.
      if (!this.appActive) {
        return false;
      }
      // Show the alert if the upload service is inactive and the user hits a route that handles uploads.
      if (!this.uploadSvcActive && /replace|upload/.test(this.$route.path)) {
        return true;
      }
      return false;
    },

    viewKey: function () {
      // console.log(
      //   'APP vue says: fullPath:',
      //   this.$route.fullPath + '--' + this.viewKeyStore
      // );
      return this.$route.fullPath + '--' + this.viewKeyStore;
    },

    appReady: function () {
      return this.authUser && this.imageCountsLoaded;
    },
  },
  methods: {
    rerenderStatsBar: function () {
      this.statsBarKey += 1;
      // console.log('App.vue says: stats bar component rerendered');
    },
    rerenderView: function () {
      this.viewKeyStore += 1;
    },
  },

  created: async function () {
    // console.log(this.$route, this.$router);
    if (this.authUser) {
      await stateUtil.initState(this.$store, this.$fapi); // Re-set/initialize state if user does a hard page refresh.
    }
  },
};
</script>

<style>
</style>
